/**
 * All report findings in one actionable, accessible table.
 * The server—not this component—decides whether an Enterprise workspace may
 * prepare a resolution plan. Fetching a preview never authorizes execution.
 */
import { useState } from 'react';
import { Badge, Button, Card, CardHead, Text } from '@qm/ui';
import type { Finding } from '@qm/contracts/audit-analysis';
import { useApp } from '../../../lib/app-context';
import { bff } from '../../../lib/bff';
import { textFor } from './audit-view';
import type { GrowthAuditModuleView } from './GrowthAuditModuleCard';

type Preview = {
  auditId:string;
  state:'planning_only';
  executionAvailable:false;
  consentRecorded:false;
  findings:Finding[];
};

const severity = (s:Finding['severity'],t:(key:string)=>string)=>
  s==='critical'?t('shell.auditResolve.critical'):
  s==='high'?t('shell.auditResolve.high'):
  s==='medium'?t('shell.auditResolve.medium'):t('shell.auditResolve.low');

const effort = (e:Finding['effort'],t:(key:string)=>string)=>
  e==='hours'?t('shell.auditResolve.hours'):
  e==='days'?t('shell.auditResolve.days'):t('shell.auditResolve.weeks');

export function ResolveFindings({
  auditId, findings, module, writer,
}:{
  auditId:string;
  findings:readonly Finding[];
  module?:GrowthAuditModuleView|null;
  writer:boolean;
}) {
  const { t, locale } = useApp();
  const loc = locale==='ar'?'ar':'en';
  const canPlan = writer && module?.capabilities.resolvePlan===true;
  const [selected,setSelected]=useState<Finding|null>(null);
  const [busy,setBusy]=useState(false);
  const [problem,setProblem]=useState(false);
  const localized = (v:Finding['title']) => textFor(v,loc)?.text ?? '';
  async function openPlan(findingId:string) {
    if (!canPlan || busy) return;
    setBusy(true);setProblem(false);setSelected(null);
    try {
      const plan=await bff<Preview>(('/v1/growth-audit/jobs/'+encodeURIComponent(auditId)+'/resolve/preview') as `/v1/${string}`);
      if (plan.executionAvailable!==false || plan.state!=='planning_only' || plan.consentRecorded!==false) throw new Error('invalid_preview');
      const chosen=plan.findings.find((f)=>f.id===findingId);
      if (!chosen) throw new Error('missing_finding');
      setSelected(chosen);
    } catch {
      setProblem(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card aria-labelledby="qm-all-findings">
      <CardHead id="qm-all-findings" title={t('shell.auditResolve.title')}
        sub={t('shell.auditResolve.subtitle')} />
      {!findings.length ? <Text tone="muted">{t('shell.auditResolve.empty')}</Text> : (
        <div className="qm-audit__resolve-table-wrap">
          <table className="qm-audit__resolve-table">
            <caption>{t('shell.auditResolve.tableCaption')}</caption>
            <thead><tr>
              <th scope="col">{t('shell.auditResolve.finding')}</th>
              <th scope="col">{t('shell.auditResolve.criticality')}</th>
              <th scope="col">{t('shell.auditResolve.effort')}</th>
              <th scope="col">{t('shell.auditResolve.evidence')}</th>
              <th scope="col">{t('shell.auditResolve.impact')}</th>
              <th scope="col">{t('shell.auditResolve.action')}</th>
            </tr></thead>
            <tbody>
              {findings.map((finding)=>(
                <tr key={finding.id}>
                  <td><bdi>{localized(finding.title)}</bdi></td>
                  <td><Badge tone={finding.severity==='critical'||finding.severity==='high'?'warning':'neutral'}>
                    {severity(finding.severity,t)}
                  </Badge></td>
                  <td>{effort(finding.effort,t)}</td>
                  <td><bdi>{localized(finding.evidence)}</bdi></td>
                  <td><bdi>{localized(finding.impact)}</bdi></td>
                  <td><Button type="button" disabled={!canPlan||busy} onClick={()=>void openPlan(finding.id)}>
                    {t('shell.auditResolve.resolve')}
                  </Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!canPlan && <Text tone="muted">{t('shell.auditResolve.locked')}</Text>}
      {problem && <Text role="alert">{t('shell.auditResolve.problem')}</Text>}
      {selected && (
        <section className="qm-audit__resolve-preview" aria-label={t('shell.auditResolve.preview')}>
          <h3>{t('shell.auditResolve.preview')} — <bdi>{localized(selected.title)}</bdi></h3>
          <p><strong>{t('shell.auditResolve.owner')}:</strong> {selected.owner}</p>
          <p><strong>{t('shell.auditResolve.fix')}:</strong> <bdi>{localized(selected.fix)}</bdi></p>
          <ol>{selected.steps.map((step,i)=><li key={i}><bdi>{localized(step)}</bdi></li>)}</ol>
          <Text tone="muted">{t('shell.auditResolve.consentRequired')}</Text>
          <Button type="button" variant="secondary" onClick={()=>setSelected(null)}>{t('shell.auditResolve.close')}</Button>
        </section>
      )}
    </Card>
  );
}

